import { useEffect, useState } from "react";
import CenteredLoader from "../common/components//CenteredLoader";
import AuthProvider from "../common/services/auth/AuthProvider/Index";
import { Button } from "../common/components/Form/Button";
import { useNavigate } from 'react-router-dom';
import { formatAnyDateToLong, formatAnyDateToTime } from '../common/utils';

interface HistoryProps {
  className?: string;
}

interface HistoryData {
  tracking_number: string;
  serial_number: string;
  lot_number: string;
  status: string;
  claim_timestamp: number;
  results_timestamp: number;
}

const History: React.FC<HistoryProps> = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [resultLoading, setResultLoading] = useState(false);
  const [historyData, setHistoryData] = useState<HistoryData[]>([]);

  useEffect(() => {
    setLoading(true);

    async function fetchData() {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/completed`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setHistoryData(data.data);
        } else {
          console.error("Failed to fetch data from the API");
        }
      } catch (error) {
        console.error("Error while fetching data:", error);
      }
    }

    fetchData().then(() => {
      setLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <main className="mx-auto px-4 sm:px-6 lg:px-8 py-8" style={{ maxWidth: '1440px' }}>
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-3 mb-2">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-brand-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-brand-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Test Results</h1>
            </div>
          </div>
          <p className="text-gray-600 text-base sm:text-lg">View and manage your post-vasectomy test results.</p>
        </div>

        {/* Loading State */}
        {(loading || resultLoading) && (
          <CenteredLoader 
            className="flex" 
            size="48" 
            showMessage={true} 
            message={loading ? "Loading your test results..." : "Preparing results..."} 
          />
        )}

        {/* Results Section */}
        {!loading && (
          <div className="space-y-6">
            {historyData.length > 0 ? (
              <div className="grid gap-3 sm:gap-4 lg:gap-5 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {historyData.map((item) => (
                  <div key={item.serial_number} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
                    {/* Card Header */}
                    <div className="p-3 sm:p-4 lg:p-5 border-b border-gray-100">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 mb-3">
                        <div className="flex items-center space-x-2">
                          <div className="p-1.5 bg-blue-100 rounded-lg flex-shrink-0">
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-4.5 lg:h-4.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 truncate">Post Vasectomy</h3>
                            <p className="text-xs sm:text-sm text-gray-500">Semen Analysis Test</p>
                          </div>
                        </div>
                        {item.status === "preparingForShippingToLab" ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 self-start sm:self-auto">
                            <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            Pending
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 self-start sm:self-auto">
                            <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Completed
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="p-3 sm:p-4 lg:p-5 space-y-2 sm:space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-1 sm:space-y-0">
                        <span className="text-xs sm:text-sm font-medium text-gray-500">Kit Number</span>
                        <span className="text-xs sm:text-sm font-semibold text-gray-900 font-mono break-all">{item.serial_number}</span>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-1 sm:space-y-0">
                        <span className="text-xs sm:text-sm font-medium text-gray-500">Analysis Date</span>
                        <span className="text-xs sm:text-sm font-semibold text-gray-900">
                          {item.status === "preparingForShippingToLab" ? (
                            <span className="text-yellow-600">Pending</span>
                          ) : (
                            <span className="break-words">{formatAnyDateToLong(item.results_timestamp * 1000)}</span>
                          )}
                        </span>
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-1 sm:space-y-0">
                        <span className="text-xs sm:text-sm font-medium text-gray-500">Analysis Time</span>
                        <span className="text-xs sm:text-sm font-semibold text-gray-900 font-mono break-all">{formatAnyDateToTime(item.results_timestamp ? item.results_timestamp * 1000 : null)}</span>
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div className="px-3 sm:px-4 lg:px-5 py-2 sm:py-3 bg-gray-50 border-t border-gray-100">
                      {item.status === "preparingForShippingToLab" ? (
                        <Button
                          label="Results Pending"
                          theme="secondary"
                          className="w-full justify-center py-1.5 sm:py-2 lg:py-2.5 text-xs sm:text-sm font-medium"
                          disabled={true}
                        />
                      ) : (
                        <Button
                          label="View Results"
                          theme="primary"
                          className="w-full justify-center py-1.5 sm:py-2 lg:py-2.5 text-xs sm:text-sm font-medium"
                          disabled={resultLoading}
                          onClick={async () => {
                            setResultLoading(true);
                            navigate('/results', { state: { serial_number: item.serial_number } });
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Empty State */
              <div className="rounded-2xl p-8 sm:p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="p-3 sm:p-4 bg-gray-100 rounded-full w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 flex items-center justify-center">
                    <svg className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No Test Results Yet</h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                    You haven't completed any semen analysis tests yet. Once you submit a test kit, your results will appear here.
                  </p>
                  <Button
                    label="Get Started"
                    theme="primary"
                    className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base"
                    onClick={() => navigate('/dashboard')}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default History;
